import React, { useState, useMemo } from 'react';
import API_CONFIG from '../utils/apiConfig';

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '520px',
    background: 'linear-gradient(135deg, #2a4252 0%, #1e2a35 100%)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '14px',
    padding: '22px',
    color: '#fff',
  },
  title: { margin: 0, fontSize: '20px', fontWeight: 700 },
  text: { margin: '10px 0 18px', opacity: 0.9, lineHeight: 1.45 },
  label: { display: 'block', fontSize: '13px', opacity: 0.9, marginBottom: '6px' },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.22)',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    outline: 'none',
    marginBottom: '14px',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.22)',
    background: 'rgba(0,0,0,0.25)',
    color: '#fff',
    outline: 'none',
    marginBottom: '14px',
  },
  error: {
    background: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid rgba(220, 53, 69, 0.35)',
    padding: '10px 12px',
    borderRadius: '10px',
    marginBottom: '14px',
    color: '#ffb3bd',
    fontSize: '14px',
  },
  button: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #57bedd, #57bedd)',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
};

export default function OrganizationSetupModal({ user, requireOrgSetup, onCompleted }) {
  const showFirstName = !String(user?.first_name || '').trim();
  const showSurname = !String(user?.surname || '').trim();

  const [companyType, setCompanyType] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const namesOnly = useMemo(() => !requireOrgSetup && (showFirstName || showSurname), [
    requireOrgSetup,
    showFirstName,
    showSurname,
  ]);

  const save = async () => {
    setError('');

    if (showFirstName && !String(firstName || '').trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (showSurname && !String(surname || '').trim()) {
      setError('Please enter your surname.');
      return;
    }

    const namePayload = {};
    if (showFirstName) namePayload.first_name = String(firstName).trim();
    if (showSurname) namePayload.surname = String(surname).trim();

    if (namesOnly) {
      setSaving(true);
      try {
        const resp = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl(API_CONFIG.endpoints.profileBootstrapNames),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(namePayload),
          }
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          setError(data.detail || 'Failed to save your name.');
          return;
        }
        onCompleted?.();
      } catch (e) {
        setError('Network error. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!companyType) {
      setError('Please select a company type.');
      return;
    }
    const needsOrgName = companyType === 'LTD';
    if (needsOrgName && !String(organizationName || '').trim()) {
      setError('Please enter your registered business name.');
      return;
    }

    if (companyType === 'SOLE_TRADER') {
      const fn = showFirstName ? String(firstName).trim() : String(user?.first_name || '').trim();
      const sn = showSurname ? String(surname).trim() : String(user?.surname || '').trim();
      if (!fn || !sn) {
        setError('First name and surname are required for sole traders.');
        return;
      }
    }

    setSaving(true);
    try {
      const body = {
        business_type:
          companyType === 'SOLE_TRADER'
            ? 'Personal (Sole Trader)'
            : 'Business (Ltd co or Partnership)',
        company_type: companyType === 'LTD' ? 'LTD' : null,
        organization_name: needsOrgName ? String(organizationName).trim() : null,
        ...namePayload,
      };
      const resp = await API_CONFIG.fetchWithAuth(API_CONFIG.getApiUrl('/unified-auth/organization/basic'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.detail || 'Failed to save organisation details.');
        return;
      }
      onCompleted?.();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.card}>
        <h2 style={modalStyles.title}>
          {namesOnly ? 'Complete your profile' : 'Set up your organisation'}
        </h2>
        <p style={modalStyles.text}>
          {namesOnly
            ? 'Add your first and last name so we can identify you correctly on PayCIS.'
            : 'You need to add your business type before you can use PayCIS.'}
        </p>

        {error ? <div style={modalStyles.error}>{error}</div> : null}

        {requireOrgSetup ? (
          <>
            <label style={modalStyles.label}>Company type</label>
            <select
              value={companyType}
              onChange={(e) => setCompanyType(e.target.value)}
              style={modalStyles.select}
              disabled={saving}
            >
              <option value="">Select…</option>
              <option value="LTD">Ltd company</option>
              <option value="SOLE_TRADER">Sole trader</option>
            </select>

            {companyType === 'LTD' ? (
              <>
                <label style={modalStyles.label}>Registered Business Name</label>
                <input
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  style={modalStyles.input}
                  placeholder="e.g. ACME Construction Ltd"
                  disabled={saving}
                />
              </>
            ) : null}
          </>
        ) : null}

        {showFirstName ? (
          <>
            <label style={modalStyles.label}>First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={modalStyles.input}
              placeholder="First name"
              disabled={saving}
              autoComplete="given-name"
            />
          </>
        ) : null}

        {showSurname ? (
          <>
            <label style={modalStyles.label}>Surname</label>
            <input
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              style={modalStyles.input}
              placeholder="Surname"
              disabled={saving}
              autoComplete="family-name"
            />
          </>
        ) : null}

        <button type="button" onClick={save} style={modalStyles.button} disabled={saving}>
          {saving ? 'Saving…' : 'Save and continue'}
        </button>
      </div>
    </div>
  );
}
